import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Dispatch, SetStateAction, useState } from "react";
import { createClient, getClients } from "../../Services/API/BaseClientManagement";
import LoadingBar from "../LoadingBar/LoadingBar";
import { StyledTextField } from "../TextField/TextField";
import { MAX_URL_LENGTH, validateHostname, validateUrl } from "../../Helpers/UrlValidator";
import { EditBaseClient } from "../../Types/BaseClientManagement";
import { BillingTier } from "../../Enums/BillingTier";
import DropdownComponent from "../Dropdown/DropdownComponent";
import { useSelector } from "react-redux";
import { selectUserInformation } from "../../State/Slices/UserInformationSlice";
import { useClientBillingTiers, useGetBillingTierByName } from "../../Helpers/billingTierDefaultPricing";
import { ClientBillingTier } from "../../Types/ClientBilling";
import { validateEmail } from "../../Helpers/emailValidator";

interface CreateBaseClientModalProps {
  openModal: Dispatch<SetStateAction<boolean>>;
  setData: Dispatch<SetStateAction<any[]>>;
  setShowSignifier: Dispatch<SetStateAction<boolean>>;
  setShowError: Dispatch<SetStateAction<boolean>>;
  createProcessing: boolean;
  setCreateProcessing: Dispatch<SetStateAction<boolean>>;
}

const useStyles = makeStyles((theme) => ({
  error: {
    color: theme.palette.error.main,
  },
  headline: {
    fontSize: "2rem",
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  root: {
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
    justifyContent: "space-between",
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },
  sensitiveButton: {
    [theme.breakpoints.down("sm")]: {
      maxWidth: "none",
      width: "100%",
    },
    marginTop: "20px",
    width: "200px",
  },
  textField: {
    "& .Mui-error": {
      color: "rgba(255,0,0,.7)",
    },
    "& .MuiFormHelperText-root": {
      color: "rgba(255,0,0,.7)",
    },
    "& .MuiInput-underline.Mui-error:after": {
      borderBottomColor: "rgba(255,0,0,.7) !important",
    },
  },
}));

export default function CreateBaseClientModal({
  openModal,
  setData,
  setShowSignifier,
  setShowError,
  createProcessing,
  setCreateProcessing,
}: CreateBaseClientModalProps) {
  const classes = useStyles();
  const { "http://nasrecruitment.io/activateId": userId } = useSelector(selectUserInformation);

  const [clientName, setClientName] = useState("");
  const [submissionAttempted, setSubmissionAttempted] = useState(false);
  const [showInputError, setShowInputError] = useState(false);
  const [includeAnalytics, setIncludeAnalytics] = useState(false);
  const [includeCareers, setIncludeCareers] = useState(false);
  const [includeClinicalAnalytics, setIncludeClinicalAnalytics] = useState(false);
  const [includeCRM, setIncludeCRM] = useState(false);

  const { tiers } = useClientBillingTiers();
  const [tier, setTier] = useState(BillingTier.Initiate);
  const tierData = useGetBillingTierByName(tier) ?? ({} as ClientBillingTier);
  const [monthlyPrice, setMonthlyPrice] = useState(tierData.monthlyBasePrice);
  const [emailOverageRate, setEmailOverageRate] = useState(tierData.emailRate);
  const [emailQuota, setEmailQuota] = useState(tierData.emailAllowance);
  const [textOverageRate, setTextOverageRate] = useState(tierData.textRate);
  const [textQuota, setTextQuota] = useState(tierData.textAllowance);

  const [emailDomain, setEmailDomain] = useState("");
  const [replyToDomain, setReplyToDomain] = useState("");
  const [shortUrlDomain, setShortUrlDomain] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [analyticsCode, setAnalyticsCode] = useState("");
  const [analyticsG4PropertyID, setAnalyticsG4PropertyID] = useState("");
  const [termsOfServiceUrl, setTermsOfServiceUrl] = useState("");
  const [privacyPolicyUrl, setPrivacyPolicyUrl] = useState("");
  const [privacyPolicyTouched, setPrivacyPolicyTouched] = useState(false);
  const [accountDirectorName, setAccountDirectorName] = useState("");
  const [accountDirectorEmail, setAccountDirectorEmail] = useState("");

  function isInputValid() {
    return (
      clientName.trim().length > 0 &&
      emailQuota >= 0 &&
      textQuota >= 0 &&
      validateHostname(emailDomain) &&
      validateHostname(replyToDomain) &&
      validateHostname(shortUrlDomain) &&
      (!accountDirectorEmail || validateEmail(accountDirectorEmail)) &&
      validateUrl(privacyPolicyUrl) &&
      (!termsOfServiceUrl || validateUrl(termsOfServiceUrl))
    );
  }

  function saveClientJson() {
    //TODO this should be a more robust validation method, but maybe it can wait till a Client Creation Wizard Revamp?
    if (!submissionAttempted) {
      setSubmissionAttempted(true);
    }

    if (!isInputValid()) {
      setShowErrorModal(true);
      setShowInputError(clientName.trim().length === 0);
      return;
    }

    let json = {
      client: {
        clientName: clientName,
        emailQuota: emailQuota,
        emailRate: emailOverageRate,
        hasAnalytics: includeAnalytics,
        hasCareers: includeCareers,
        hasClinicalAnalytics: includeClinicalAnalytics,
        hasCrm: includeCRM,
        monthlyBasePrice: monthlyPrice,
        emailDomain,
        replyToDomain,
        shortUrlDomain: shortUrlDomain,
        textQuota: textQuota,
        textRate: textOverageRate,
        tierName: tier,
        analyticsCode: analyticsCode,
        analyticsG4PropertyID: analyticsG4PropertyID,
        termsOfServiceUrl: termsOfServiceUrl,
        privacyPolicyUrl: privacyPolicyUrl,
        id: null,
        accountDirectorEmail,
        accountDirectorName,
        userId,
      },
    } as EditBaseClient;
    submitData(json);
  }

  const submitData = (json: EditBaseClient) => {
    setCreateProcessing(true);
    setShowInputError(false);
    createClient(json).then((isSuccess) => {
      setShowSignifier(true);
      if (isSuccess) {
        getClients()
          .then((clientList) => {
            setData(clientList);
          })
          .catch((err) => {
            // Client was created; only the list refresh failed.
            console.error("Failed to refresh clients after create:", err);
          })
          .finally(() => {
            setCreateProcessing(false);
            openModal(false);
          });
      } else {
        setShowError(true);
        setCreateProcessing(false);
      }
    });
  };

  function changeBillingTier(tier: BillingTier) {
    setTier((_) => tier);
    const billingTier = tiers.find((t) => t.tierName === tier);
    if (billingTier) {
      setMonthlyPrice(billingTier.monthlyBasePrice);
      setEmailOverageRate(billingTier.emailRate);
      setEmailQuota(billingTier.emailAllowance);
      setTextOverageRate(billingTier.textRate);
      setTextQuota(billingTier.textAllowance);
    }
  }

  return (
    <div className={classes.root}>
      <Grid>
        <LoadingBar loading={createProcessing} />
        <Card>
          <CardContent>
            <Grid container spacing={1}>
              <Grid xs={12} item>
                <Typography variant="h2" className={classes.headline}>
                  Create Client
                </Typography>
              </Grid>
              <Grid xs={12} item>
                <StyledTextField
                  InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                  className={classes.textField}
                  placeholder="Client Name"
                  label="Client Name"
                  variant="standard"
                  fullWidth
                  required
                  error={showInputError && clientName === ""}
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  data-test="create-base-client-name-input"
                />
                {showInputError && clientName === "" && (
                  <FormHelperText className={classes.error}>Client Name Required</FormHelperText>
                )}
              </Grid>
              <Grid item xs={12} style={{ paddingTop: 20 }}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <FormHelperText>Account Director Information</FormHelperText>
              </Grid>
              <Grid xs={6} item>
                <StyledTextField
                  InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                  className={classes.textField}
                  placeholder="Name"
                  label="Name"
                  variant="standard"
                  fullWidth
                  value={accountDirectorName}
                  onChange={(e) => setAccountDirectorName(e.target.value)}
                  data-test="create-base-account-director-name-input"
                />
              </Grid>
              <Grid xs={6} item>
                <StyledTextField
                  InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                  className={classes.textField}
                  placeholder="Email"
                  label="Email"
                  variant="standard"
                  fullWidth
                  error={!!accountDirectorEmail && !validateEmail(accountDirectorEmail)}
                  value={accountDirectorEmail}
                  onChange={(e) => setAccountDirectorEmail(e.target.value)}
                  data-test="create-base-account-director-email-input"
                />
                {accountDirectorEmail && !validateEmail(accountDirectorEmail) && (
                  <FormHelperText className={classes.error}>Invalid email address</FormHelperText>
                )}
              </Grid>
              <Grid item xs={12} style={{ paddingTop: 20 }}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <FormHelperText>Client Products</FormHelperText>
              </Grid>
              <Grid container item spacing={1}>
                <Grid xs={12} sm={6} item>
                  <FormControlLabel
                    label="Include Analytics"
                    control={
                      <Checkbox
                        checked={includeAnalytics}
                        data-test="create-base-client-analytics-check"
                        color="primary"
                        onChange={(e) => setIncludeAnalytics(e.target.checked)}
                      />
                    }
                  />
                </Grid>
                <Grid xs={12} sm={6} item>
                  <FormControlLabel
                    label="Include Careers"
                    control={
                      <Checkbox
                        checked={includeCareers}
                        data-test="create-base-client-careers-check"
                        color="primary"
                        onChange={(e) => setIncludeCareers(e.target.checked)}
                      />
                    }
                  />
                </Grid>
                <Grid xs={12} sm={6} item>
                  <FormControlLabel
                    label="Include Clinical Analytics"
                    control={
                      <Checkbox
                        checked={includeClinicalAnalytics}
                        data-test="create-base-client-clinicalanalytics-check"
                        color="primary"
                        onChange={(e) => setIncludeClinicalAnalytics(e.target.checked)}
                      />
                    }
                  />
                </Grid>
                <Grid xs={12} sm={6} item>
                  <FormControlLabel
                    label="Include CRM"
                    control={
                      <Checkbox
                        checked={includeCRM}
                        data-test="create-base-client-crm-check"
                        color="primary"
                        onChange={(e) => setIncludeCRM(e.target.checked)}
                      />
                    }
                  />
                </Grid>
                <Grid container item spacing={1}>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={12}>
                    <FormHelperText>Client Policies</FormHelperText>
                  </Grid>
                  <Grid item container spacing={1} xs={12}>
                    <Grid item xs={12} lg={6}>
                      <TextField
                        InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                        label="Terms Of Service URL"
                        fullWidth
                        placeholder="Terms Of Service URL"
                        value={termsOfServiceUrl}
                        onChange={(e) => setTermsOfServiceUrl(e.target.value)}
                        error={!!termsOfServiceUrl && !validateUrl(termsOfServiceUrl)}
                      />
                      {!!termsOfServiceUrl && !validateUrl(termsOfServiceUrl) && (
                        <FormHelperText className={classes.error}>Invalid URL</FormHelperText>
                      )}
                    </Grid>
                    <Grid item xs={12} lg={6}>
                      <TextField
                        InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                        label="Privacy Policy URL"
                        fullWidth
                        placeholder="Privacy Policy URL"
                        value={privacyPolicyUrl}
                        onChange={(e) => {
                          setPrivacyPolicyUrl(e.target.value);
                          if (!privacyPolicyTouched) {
                            setPrivacyPolicyTouched(true);
                          }
                        }}
                        required={true}
                        error={
                          (submissionAttempted || privacyPolicyTouched) && !validateUrl(privacyPolicyUrl)
                        }
                      />
                      {(submissionAttempted || privacyPolicyTouched) && !validateUrl(privacyPolicyUrl) && (
                        <FormHelperText className={classes.error}>Invalid URL</FormHelperText>
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid container spacing={1}>
              <Grid xs={12} style={{ paddingTop: 20 }}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <FormHelperText>Billing Tier</FormHelperText>
              </Grid>
              <Grid item xs={12} sm={6}>
                <DropdownComponent
                  currValue={tier}
                  items={tiers}
                  handleChange={(name: string) =>
                    changeBillingTier(
                      tiers.find((t) => t.tierName === name)?.tierName ?? BillingTier.Initiate
                    )
                  }
                  property="tierName"
                  title="Tier"
                  data-test={"editclient-tier-dropdown"}
                  error={emailQuota < 0}
                  multi={false}
                  itemIdSelector={(item: ClientBillingTier) => item.tierName}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <StyledTextField
                  fullWidth
                  InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                  error={monthlyPrice < 0}
                  type="number"
                  label="Monthly Price"
                  value={monthlyPrice}
                  variant="standard"
                  onChange={(e) => setMonthlyPrice(Number(e.target.value))}
                  data-test="create-base-client-monthlyprice-input"
                  inputProps={{
                    min: 0,
                    step: 1000,
                  }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
                {monthlyPrice < 0 && (
                  <FormHelperText className={classes.error}>Negative numbers are not allowed</FormHelperText>
                )}
              </Grid>
              <Grid item container spacing={1} xs={12}>
                <Grid item xs={12} lg={6}>
                  <StyledTextField
                    fullWidth
                    InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                    error={emailQuota < 0}
                    type="number"
                    label="Email Quota"
                    value={emailQuota}
                    variant="standard"
                    onChange={(e) => setEmailQuota(Number(e.target.value))}
                    data-test="create-base-client-emailquotas-input"
                    inputProps={{
                      min: 0,
                      step: 1000,
                      default: 100000,
                    }}
                  />
                  {emailQuota < 0 && (
                    <FormHelperText className={classes.error}>
                      Negative numbers are not allowed
                    </FormHelperText>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StyledTextField
                    fullWidth
                    InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                    error={emailOverageRate < 0}
                    type="number"
                    label="Email Overage Rate"
                    value={emailOverageRate}
                    variant="standard"
                    onChange={(e) => setEmailOverageRate(Number(e.target.value))}
                    data-test="create-base-client-emailrate-input"
                    inputProps={{
                      min: 0.0,
                      step: 0.01,
                    }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                  {emailOverageRate < 0 && (
                    <FormHelperText className={classes.error}>
                      Negative numbers are not allowed
                    </FormHelperText>
                  )}
                </Grid>
                <Grid item xs={12} lg={6}>
                  <StyledTextField
                    fullWidth
                    InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                    error={textQuota < 0}
                    type="number"
                    label="Texting Quota"
                    value={textQuota}
                    variant="standard"
                    onChange={(e) => setTextQuota(Number(e.target.value))}
                    data-test="create-base-client-textquotas-input"
                    inputProps={{
                      min: 0,
                      step: 1000,
                    }}
                  />
                  {textQuota < 0 && (
                    <FormHelperText className={classes.error}>
                      Negative numbers are not allowed
                    </FormHelperText>
                  )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StyledTextField
                    fullWidth
                    InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                    error={textOverageRate < 0}
                    type="number"
                    label="Text Overage Rate"
                    value={textOverageRate}
                    variant="standard"
                    onChange={(e) => setTextOverageRate(Number(e.target.value))}
                    data-test="create-base-client-textrate-input"
                    inputProps={{
                      min: 0.0,
                      step: 0.01,
                    }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                    }}
                  />
                  {textOverageRate < 0 && (
                    <FormHelperText className={classes.error}>
                      Negative numbers are not allowed
                    </FormHelperText>
                  )}
                </Grid>
              </Grid>
            </Grid>
            <Grid container spacing={1}>
              <Grid xs={12} style={{ paddingTop: 20 }}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <FormHelperText>Short URL Domain</FormHelperText>
              </Grid>
              <Grid item xs={12}>
                <StyledTextField
                  InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                  className={classes.textField}
                  type="text"
                  label="Domain"
                  defaultValue=""
                  value={shortUrlDomain}
                  variant="standard"
                  fullWidth
                  onChange={(e) => setShortUrlDomain(e.target.value)}
                  data-test="edit-base-client-shortio-domain"
                  error={!validateHostname(shortUrlDomain)}
                />
                {!validateHostname(shortUrlDomain) && (
                  <FormHelperText className={classes.error}>
                    Domain is not valid
                    {shortUrlDomain.length > MAX_URL_LENGTH
                      ? ". Must be less than or equal 150 characters"
                      : ""}
                  </FormHelperText>
                )}
              </Grid>
            </Grid>
            <Grid container spacing={1}>
              <Grid xs={12} style={{ paddingTop: 20 }}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <FormHelperText>CRM Email Domains</FormHelperText>
              </Grid>
              <Grid item xs={12} sm={6}>
                <StyledTextField
                  InputLabelProps={{
                    style: { color: "rgba(0,0,0,.6)" },
                    title: "Configure the email domain you want to use when sending emails from this client",
                  }}
                  className={classes.textField}
                  type="text"
                  label="Sender Email Domain"
                  placeholder="careers.client-name.com"
                  value={emailDomain}
                  variant="standard"
                  fullWidth
                  onChange={(e) => setEmailDomain(e.target.value)}
                  data-test="create-base-client-email-domain"
                  error={!validateHostname(emailDomain)}
                />
                {!validateHostname(emailDomain) && (
                  <FormHelperText className={classes.error}>
                    Domain is not valid
                    {emailDomain.length > MAX_URL_LENGTH ? ". Must be less than or equal 150 characters" : ""}
                  </FormHelperText>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <StyledTextField
                  InputLabelProps={{
                    style: { color: "rgba(0,0,0,.6)" },
                    title:
                      "Configure the email domain you want to use when receiving emails from CRM candidate replies",
                  }}
                  className={classes.textField}
                  type="text"
                  label="Reply-To Email Domain"
                  placeholder="careers.client-name.com"
                  value={replyToDomain}
                  variant="standard"
                  fullWidth
                  onChange={(e) => setReplyToDomain(e.target.value)}
                  data-test="create-base-client-reply-to-domain"
                  error={!validateHostname(replyToDomain)}
                />
                {!validateHostname(replyToDomain) && (
                  <FormHelperText className={classes.error}>
                    Domain is not valid
                    {replyToDomain.length > MAX_URL_LENGTH
                      ? ". Must be less than or equal 150 characters"
                      : ""}
                  </FormHelperText>
                )}
              </Grid>
            </Grid>
            {includeAnalytics ? (
              <Grid container spacing={1}>
                <Grid xs={12} style={{ paddingTop: 20 }}>
                  <Divider />
                </Grid>
                <Grid item xs={12}>
                  <FormHelperText>Analytics Setup</FormHelperText>
                </Grid>
                <Grid item xs={12}>
                  <StyledTextField
                    style={{ width: "100%" }}
                    multiline
                    InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                    label="Analytics Code"
                    value={analyticsCode}
                    variant="standard"
                    onChange={(e) => setAnalyticsCode(e.target.value)}
                    data-test="create-base-client-analyticscode-input"
                  />
                </Grid>
                <Grid item xs={12}>
                  <StyledTextField
                    style={{ width: "100%" }}
                    InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                    label="G4 Property ID"
                    value={analyticsG4PropertyID}
                    variant="standard"
                    onChange={(e) => setAnalyticsG4PropertyID(e.target.value)}
                    data-test="create-base-client-analyticsg4-input"
                  />
                </Grid>
              </Grid>
            ) : null}
            <Grid xs={12} item container style={{ justifyContent: "flex-end" }}>
              <Grid xs={12} sm={3} item>
                <Button
                  style={{ marginTop: "30px", width: "95%" }}
                  type="submit"
                  variant="outlined"
                  color="primary"
                  onClick={() => openModal(false)}
                  disabled={createProcessing}
                  data-test="create-base-client-cancel-button"
                >
                  Cancel
                </Button>
              </Grid>
              <Grid xs={12} sm={3} item>
                <Button
                  className={classes.sensitiveButton}
                  style={{
                    marginTop: "30px",
                    width: "95%",
                    whiteSpace: "nowrap",
                  }}
                  type="submit"
                  variant="contained"
                  color="primary"
                  onClick={() => saveClientJson()}
                  disabled={createProcessing}
                  data-test="create-base-client-name-create-button"
                >
                  Create Client
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
      <Dialog open={showErrorModal} onClose={() => setShowErrorModal(false)}>
        <DialogContent>Please correct fields with errors before saving.</DialogContent>
        <DialogActions>
          <Button onClick={() => setShowErrorModal(false)} type="submit" color="primary" variant="contained">
            Okay
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
